import Table from "@tiptap/extension-table"
import TableCell from "@tiptap/extension-table-cell"
import TableHeader from "@tiptap/extension-table-header"
import TableRow from "@tiptap/extension-table-row"
import YoutubeExtension from "@tiptap/extension-youtube"
import { generateHTML, generateJSON } from "@tiptap/html"
import TurndownService from "turndown"

import {
	getDefaultExtensions,
	ImageWithLayout,
	InlineImageExtension,
	VideoExtension,
} from "@productlane/lib/src/browser"
import { addBadgeMarkdownRules } from "@productlane/lib/src/tiptap/mintlify-markdown/badge"
import { addCalloutsMarkdownRules } from "@productlane/lib/src/tiptap/mintlify-markdown/callouts"
import { addColorMarkdownRules } from "@productlane/lib/src/tiptap/mintlify-markdown/color"
import { addFieldsMarkdownRules } from "@productlane/lib/src/tiptap/mintlify-markdown/fields"
import { addFrameMarkdownRules } from "@productlane/lib/src/tiptap/mintlify-markdown/frame"
import { addIconMarkdownRules } from "@productlane/lib/src/tiptap/mintlify-markdown/icon"
import { addMermaidMarkdownRules } from "@productlane/lib/src/tiptap/mintlify-markdown/mermaid"
import { addPromptMarkdownRules } from "@productlane/lib/src/tiptap/mintlify-markdown/prompt"
import { addTileMarkdownRules } from "@productlane/lib/src/tiptap/mintlify-markdown/tile"
import { addTooltipMarkdownRules } from "@productlane/lib/src/tiptap/mintlify-markdown/tooltip"
import { addTreeMarkdownRules } from "@productlane/lib/src/tiptap/mintlify-markdown/tree"
import { addUpdateMarkdownRules } from "@productlane/lib/src/tiptap/mintlify-markdown/update"
import { addPanelMarkdownRules } from "@productlane/lib/src/tiptap/panel-markdown"

import { renderMarkdownHtml } from "./markdown-html"

// API stays markdown-in/markdown-out; storage is Tiptap JSON (changelogs) or HTML (articles). Round-trip is lossy for Tiptap-only content.

function getExtensions() {
	return [
		...getDefaultExtensions({ image: false, heading: true }),
		// Block, like the dashboard's own image node. renderMarkdownHtml already lifts
		// every image out of the <p> marked wrapped it in, so an inline node here
		// only leaves one the editor's schema has nowhere to put.
		ImageWithLayout.configure({ allowBase64: true }),
		InlineImageExtension,
		Table,
		TableRow,
		TableHeader,
		TableCell,
		VideoExtension,
		YoutubeExtension,
	]
}

export function markdownToTiptapJson(markdown: string): Promise<unknown> {
	return new Promise((resolve) =>
		resolve(generateJSON(renderMarkdownHtml(markdown), getExtensions())),
	)
}

export function markdownToHtml(markdown: string): Promise<string> {
	return new Promise((resolve) => resolve(renderMarkdownHtml(markdown)))
}

const KEPT_IMG_ATTRIBUTES = new Set(["src", "alt", "title"])

// Anything markdown cannot express (size, background-removal sources) would be
// dropped on the way out and gone on the next write.
function imageNeedsRawHtml(node: HTMLElement): boolean {
	return Array.from(node.attributes).some(
		(attr) => !KEPT_IMG_ATTRIBUTES.has(attr.name),
	)
}

// Tiptap renders an accordion as <details>; older rows carry the div form.
function isDetailsBlock(node: Node): boolean {
	if (node.nodeName === "DETAILS") return true
	return (
		node.nodeName === "DIV" &&
		(node as HTMLElement).getAttribute("data-type") === "details"
	)
}

// marked ends a raw HTML block at the first blank line, so a blank line inside
// one (a code block, most often) travels as entities and decodes on render.
function rawHtml(node: Node): string {
	return (node as HTMLElement).outerHTML.replace(/\n([ \t]*\n)+/g, (run) =>
		run.replace(/\n/g, "&#10;"),
	)
}

// `accent-bullets` and `steps-list` carry the house list treatment. Markdown has
// no syntax for either, so a plain `-` list on the way out drops the styling.
function isHouseStyledList(node: Node): boolean {
	if (node.nodeName !== "UL" && node.nodeName !== "OL") return false
	const classes = (node as HTMLElement).getAttribute("class") ?? ""
	if (!/\b(accent-bullets|steps-list)\b/.test(classes)) return false
	for (let parent = node.parentNode; parent; parent = parent.parentNode) {
		if (parent.nodeName === "LI") return false
	}
	return true
}

function isIframeWrapper(node: Node): boolean {
	return (
		node.nodeName === "DIV" &&
		(node as HTMLElement).hasAttribute("data-iframe-embed")
	)
}

// Tiptap parses these back only through their wrapper, so turndown descending
// into one loses the gallery or drops the embed on the next write.
function isWrappedAtom(node: Node): boolean {
	const el = node as HTMLElement
	if (node.nodeName === "FIGURE") return el.hasAttribute("data-image-gallery")
	return node.nodeName === "DIV" && el.hasAttribute("data-youtube-video")
}

function tableRows(table: HTMLElement): HTMLElement[] {
	return Array.from(table.querySelectorAll("tr")) as HTMLElement[]
}

// A pipe table holds one line per row, so any block inside a cell collapses.
function cellContent(content: string): string {
	return content.trim().replace(/\n+/g, " ").replace(/\|/g, "\\|")
}

function hasMergedCells(table: HTMLElement): boolean {
	return tableRows(table).some((row) =>
		Array.from(row.children).some((cell) => {
			const span = (attr: string) => Number(cell.getAttribute(attr) ?? "1")
			return span("colspan") > 1 || span("rowspan") > 1
		}),
	)
}

// Shared instance: construction parses rules, so reuse it.
const turndown = (() => {
	const td = new TurndownService({
		headingStyle: "atx",
		codeBlockStyle: "fenced",
		bulletListMarker: "-",
		emDelimiter: "_",
		hr: "---",
		br: "\n",
	})
	// Inline GFM strikethrough rather than pulling `turndown-plugin-gfm`.
	td.addRule("strikethrough", {
		filter: ["s", "del" as never],
		replacement: (content) => `~~${content}~~`,
	})
	// Checklist items: turndown's list rule would drop the checkbox state.
	td.addRule("taskListItem", {
		filter: (node) =>
			node.nodeName === "LI" &&
			["todo_item", "taskItem"].includes(node.getAttribute("data-type") ?? ""),
		replacement: (content, node) => {
			const el = node as HTMLElement
			const done =
				el.getAttribute("data-done") === "true" ||
				el.getAttribute("data-checked") === "true"
			const text = content
				.replace(/^\n+/, "")
				.replace(/\n+$/, "")
				.replace(/\n/g, "\n  ")
			return `- [${done ? "x" : " "}] ${text}\n`
		},
	})
	// Round-trip verbatim: markdown has no syntax for these, and the next write
	// back would drop whatever the reader could not see.
	td.addRule("rawBlockHtml", {
		filter: (node) =>
			isDetailsBlock(node) ||
			isIframeWrapper(node) ||
			isHouseStyledList(node) ||
			isWrappedAtom(node) ||
			node.nodeName === "IFRAME",
		replacement: (_content, node) => `\n\n${rawHtml(node)}\n\n`,
	})
	// A bare <video> line comes back from marked inside a paragraph, where the
	// block video node has nowhere to live; the wrapper keeps it top level.
	td.addRule("video", {
		filter: "video",
		replacement: (_content, node) => `\n\n<div>${rawHtml(node)}</div>\n\n`,
	})
	td.addRule("richImage", {
		filter: (node) => node.nodeName === "IMG" && imageNeedsRawHtml(node),
		replacement: (_content, node) => (node as HTMLElement).outerHTML,
	})
	addPanelMarkdownRules(td)
	// Added after rawBlockHtml, so these win over its <details> and <div> matches.
	addBadgeMarkdownRules(td)
	addCalloutsMarkdownRules(td)
	addColorMarkdownRules(td)
	addFieldsMarkdownRules(td)
	addFrameMarkdownRules(td)
	addIconMarkdownRules(td)
	addMermaidMarkdownRules(td)
	addPromptMarkdownRules(td)
	addTileMarkdownRules(td)
	addTooltipMarkdownRules(td)
	addTreeMarkdownRules(td)
	addUpdateMarkdownRules(td)
	td.keep(["mark"])
	td.addRule("tableSection", {
		filter: ["thead", "tbody", "tfoot"],
		replacement: (content) => content,
	})
	td.addRule("tableCell", {
		filter: ["th", "td"],
		replacement: (content) => `| ${cellContent(content)} `,
	})
	td.addRule("tableRow", {
		filter: "tr",
		replacement: (content, node) => {
			const row = `${content}|`
			const table = (node as HTMLElement).closest("table")
			if (!table || tableRows(table)[0] !== node) return `\n${row}`
			const divider = `\n|${" --- |".repeat(node.children.length)}`
			return `\n${row}${divider}`
		},
	})
	td.addRule("table", {
		filter: "table",
		replacement: (content, node) => {
			// A pipe table cannot express a merged cell, so those stay as HTML.
			if (hasMergedCells(node as HTMLElement)) {
				return `\n\n${rawHtml(node)}\n\n`
			}
			return `\n\n${content.trim()}\n\n`
		},
	})
	return td
})()

export function htmlToMarkdown(html: string | null | undefined): string {
	if (!html) return ""
	return turndown.turndown(html)
}

// Accepts a JSON document, the string "null", or actual null (legacy rows).
export function tiptapJsonToMarkdown(value: unknown): string {
	if (!value || value === "null") return ""
	try {
		const html = generateHTML(
			value as Parameters<typeof generateHTML>[0],
			getExtensions(),
		)
		return htmlToMarkdown(html)
	} catch {
		return ""
	}
}

export function tiptapJsonToHtml(value: unknown): string {
	if (!value || value === "null") return ""
	try {
		return generateHTML(
			value as Parameters<typeof generateHTML>[0],
			getExtensions(),
		)
	} catch {
		return ""
	}
}

export function htmlToTiptapJson(html: string): unknown {
	if (!html) return null
	return generateJSON(html, getExtensions())
}
